import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from "firebase/firestore";

import { db } from "integrations/firebase/firestore";

import type { AgendaItemDraft } from "./agenda-import";

/**
 * Rows per batched write.
 *
 * Bounded by the rules, not by Firestore's 500 writes per batch. Each
 * agenda_items create makes two access calls (the member `get` in
 * isEventAdmin and the deletion-state `exists` in isEventWritable), and a
 * batched write is allowed 20 in production. Identical calls "may" be cached
 * according to the documentation, which is no promise, so 10 creates keep a
 * batch within 20 even when nothing is cached. Over the limit, every batch
 * and every Retry would be refused with permission-denied, and the emulator
 * enforces no limit, so no local test would show it.
 */
export const AGENDA_IMPORT_BATCH_SIZE = 10;

/** A fresh document id for a row, minted before its first write attempt. */
export function newAgendaItemId(eventId: string): string {
  return doc(collection(db, "events", eventId, "agenda_items")).id;
}

export type BatchOutcome = { ok: true } | { ok: false; error: string };

/**
 * Create these agenda items in one batch.
 *
 * Each document carries exactly the keys the agenda editor's create form
 * writes, and `createdAt` as the server timestamp the rules pin to
 * `request.time`. The batch is all or nothing, so a refused one leaves none
 * of its rows behind.
 */
export async function writeAgendaBatch(
  eventId: string,
  items: { id: string; draft: AgendaItemDraft }[],
): Promise<BatchOutcome> {
  const batch = writeBatch(db);
  for (const { id, draft } of items) {
    batch.set(doc(db, "events", eventId, "agenda_items", id), {
      ...draft,
      startsAt: Timestamp.fromDate(draft.startsAt),
      endsAt: Timestamp.fromDate(draft.endsAt),
      createdAt: serverTimestamp(),
    });
  }
  try {
    await batch.commit();
    return { ok: true };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : "The write failed.",
    };
  }
}

/**
 * Whether a row's document exists already.
 *
 * Asked before a retry, because a commit whose answer was lost on the network
 * may have landed. A second create of the same id would be refused by the
 * rules as an update, and a new id would put the session on the agenda twice.
 */
export async function agendaItemExists(
  eventId: string,
  id: string,
): Promise<boolean> {
  const snap = await getDoc(doc(db, "events", eventId, "agenda_items", id));
  return snap.exists();
}
